/**
 * AcceptInvite.jsx - Nexus Core Design System (AIRGROW)
 * 招待リンクからの組織参加ページ
 * URL: /invite/:token
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useFadeNavigation } from '../hooks/useFadeNavigation';
import { getInvitation, acceptInvitation } from '../services/userApi';
import AuthLayout from '../components/auth/AuthLayout';

const ROLE_LABELS = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

const MaterialIcon = ({ name, className = '', filled = false, style = {} }) => (
  <span
    className={`material-symbols-outlined ${className}`}
    style={{ fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0", ...style }}
  >
    {name}
  </span>
);

function StateHeader({ icon, title, subtitle, iconBg = 'bg-nexus-primary', iconColor = 'text-white', filled = true }) {
  return (
    <div className="flex flex-col items-center text-center mb-6">
      <div className={`w-14 h-14 ${iconBg} flex items-center justify-center rounded-full mb-4`}>
        <MaterialIcon name={icon} filled={filled} className={`${iconColor} text-[32px]`} />
      </div>
      <h1 className="text-[20px] leading-[28px] font-bold text-text-primary tracking-tight">{title}</h1>
      {subtitle && <div className="text-sm text-text-secondary mt-2">{subtitle}</div>}
    </div>
  );
}

function InvitationDetails({ invitation }) {
  return (
    <div className="bg-light-neutral rounded-lg p-4 space-y-3 mb-6">
      <div className="flex justify-between items-center">
        <span className="text-xs text-text-secondary">組織名</span>
        <span className="text-sm font-medium text-text-primary">{invitation.orgName}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs text-text-secondary">役割</span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-nexus-blue-soft text-nexus-primary">
          {ROLE_LABELS[invitation.role]}
        </span>
      </div>
      {invitation.inviterName && (
        <div className="flex justify-between items-center">
          <span className="text-xs text-text-secondary">招待者</span>
          <span className="text-sm font-medium text-text-primary">{invitation.inviterName}</span>
        </div>
      )}
    </div>
  );
}

export default function AcceptInvite() {
  const { token } = useParams();
  const { isLoggedIn, loading: authLoading } = useAuth();
  const { cardVisible, isLeaving, navigateWithFade } = useFadeNavigation();

  const [invitation, setInvitation] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('招待リンクが無効です');
      return;
    }
    getInvitation(token)
      .then((data) => setInvitation(data))
      .catch(() => setLoadError('招待リンクが無効または期限切れです'));
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      await acceptInvitation({ token, transferData: false });
      setDone(true);
      setTimeout(() => navigateWithFade('/'), 1500);
    } catch (e) {
      setAcceptError(e.message || '承認に失敗しました');
    } finally {
      setAccepting(false);
    }
  };

  // ローディング中
  if (authLoading) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-14 h-14 rounded-full border-4 border-nexus-blue-soft border-t-nexus-primary animate-spin mb-4" />
          <h1 className="text-[18px] font-semibold text-text-primary">読み込み中...</h1>
        </div>
      </AuthLayout>
    );
  }

  // エラー
  if (loadError) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <StateHeader
          icon="error"
          title="招待リンクエラー"
          iconBg="bg-red-50"
          iconColor="text-critical-red"
          subtitle={loadError}
        />
        <button
          type="button"
          disabled={isLeaving}
          onClick={() => navigateWithFade('/login')}
          className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60"
        >
          <MaterialIcon name="login" className="text-[18px]" />
          ログインページへ
        </button>
      </AuthLayout>
    );
  }

  // 招待確認中
  if (!invitation) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-14 h-14 bg-nexus-blue-soft rounded-full flex items-center justify-center mb-4">
            <MaterialIcon name="mail" filled className="text-nexus-primary text-[32px]" />
          </div>
          <h1 className="text-[18px] font-semibold text-text-primary">招待を確認中...</h1>
        </div>
      </AuthLayout>
    );
  }

  // 参加完了
  if (done) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <StateHeader
          icon="check_circle"
          title="参加完了"
          iconBg="bg-emerald-50"
          iconColor="text-active-green"
          subtitle={
            <>
              <span className="font-medium text-text-primary">{invitation.orgName}</span> に参加しました
              <p className="text-xs text-text-secondary mt-3">ホーム画面へ移動します...</p>
            </>
          }
        />
      </AuthLayout>
    );
  }

  // 未ログイン時
  if (!isLoggedIn) {
    return (
      <AuthLayout cardVisible={cardVisible}>
        <StateHeader
          icon="mail"
          title="招待が届いています"
          subtitle={
            <>
              <span className="font-medium text-text-primary">{invitation.orgName}</span> から
              {invitation.inviterName && ` ${invitation.inviterName} さんより`}
              招待されています
            </>
          }
        />

        <InvitationDetails invitation={invitation} />

        <div className="flex items-start gap-2 rounded-lg border border-nexus-blue-soft bg-nexus-blue-soft/40 px-4 py-3 text-xs text-nexus-primary mb-6">
          <MaterialIcon name="info" className="text-[18px] flex-shrink-0 mt-px text-nexus-blue" />
          <span>参加するにはログインまたは新規登録が必要です</span>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            disabled={isLeaving}
            onClick={() => navigateWithFade(`/login?redirect=/invite/${token}`)}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60"
          >
            <MaterialIcon name="login" className="text-[18px]" />
            ログインして参加
          </button>
          <button
            type="button"
            disabled={isLeaving}
            onClick={() => navigateWithFade(`/register?redirect=/invite/${token}`)}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-transparent text-text-primary border border-border-light text-sm font-semibold rounded-lg hover:bg-light-neutral active:scale-[0.98] transition-all disabled:opacity-60"
          >
            <MaterialIcon name="person_add" className="text-[18px]" />
            新規登録して参加
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ログイン済み
  return (
    <AuthLayout cardVisible={cardVisible}>
      <StateHeader
        icon="mail"
        title="組織への招待"
        subtitle={
          <>
            <span className="font-medium text-text-primary">{invitation.orgName}</span> から招待されています
          </>
        }
      />

      <InvitationDetails invitation={invitation} />

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 mb-6">
        <MaterialIcon name="warning" className="text-[18px] flex-shrink-0 mt-px text-alert-orange" />
        <span>参加すると現在の個人データは組織に移行されます</span>
      </div>

      {acceptError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-4">
          {acceptError}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => navigateWithFade('/')}
          disabled={accepting || isLeaving}
          className="flex-1 flex justify-center items-center gap-2 py-3 px-4 bg-transparent text-text-primary border border-border-light text-sm font-semibold rounded-lg hover:bg-light-neutral active:scale-[0.98] transition-all disabled:opacity-60"
        >
          キャンセル
        </button>
        <button
          type="button"
          onClick={handleAccept}
          disabled={accepting || isLeaving}
          className="flex-1 flex justify-center items-center gap-2 py-3 px-4 bg-nexus-primary text-white text-sm font-semibold rounded-lg hover:bg-nexus-primary-container active:scale-[0.98] transition-all shadow-md shadow-nexus-primary/10 disabled:opacity-60"
        >
          {accepting ? '参加中...' : (
            <>
              <MaterialIcon name="check_circle" className="text-[18px]" />
              参加する
            </>
          )}
        </button>
      </div>
    </AuthLayout>
  );
}
